import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
	ReactNode,
} from "react";
import * as Sentry from "@sentry/react";
import {
	AuthState,
	AuthResponse,
} from "@cuntas-mitool/shared";
import queryClient from "../lib/queryClient";
import { apiFetch, API_BASE_URL } from "../lib/requestFunctions";

export const TOKEN_STORAGE_KEY = "cuntas_session_token";
const PROVIDER_STORAGE_KEY = "cuntas_session_provider";

export type LoginProvider = 'microsoft' | 'google';

export interface AuthContextValue extends AuthState {
	login: (provider: LoginProvider) => Promise<void>;
	logout: () => Promise<void>;
	getToken: () => string | null;
	/** Refetch user data from the server (e.g. after subscription change) */
	refreshUser: () => Promise<void>;
	/** Fetch wrapper that auto-retries on 401 by silently re-authenticating */
	authenticatedFetch: (url: string, init?: RequestInit) => Promise<Response>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

function isUserCancellation(error: unknown): boolean {
	if (!(error instanceof Error)) return false;
	const msg = error.message.toLowerCase();
	// Office Dialog 12006 and Google/Microsoft cancellation messages
	if (msg.includes('cancelled') || msg.includes('canceled')) return true;
	// MSAL BrowserAuthError codes for popup dismissal
	const errorCode = (error as { errorCode?: string }).errorCode;
	return errorCode === 'user_cancelled' || errorCode === 'popup_window_error';
}

interface BaseAuthProviderProps {
	children: ReactNode;
	/** Platform-specific function to acquire a provider token (Microsoft access token or Google ID token). */
	acquireProviderToken: (provider: LoginProvider) => Promise<string>;
}

export function BaseAuthProvider({ children, acquireProviderToken }: BaseAuthProviderProps) {
	const [state, setState] = useState<AuthState>({
		user: null,
		token: null,
		isLoading: true,
		isAuthenticated: false,
		error: null,
	});

	const getToken = useCallback((): string | null => {
		return state.token || localStorage.getItem(TOKEN_STORAGE_KEY);
	}, [state.token]);

	const exchangeToken = useCallback(
		async (
			providerToken: string,
			provider: LoginProvider = 'microsoft',
		): Promise<AuthResponse> => {
			const endpoint = provider === 'google' ? 'google' : 'microsoft';
			console.log(`[Auth] Exchanging ${provider} token with backend...`);
			try {
				const response = await apiFetch(
					`${API_BASE_URL}/auth/${endpoint}`,
					{
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ token: providerToken }),
					},
				);

				if (!response.ok) {
					const errorText = await response.text();
					console.error("[Auth] Backend error response:", errorText);
					return { success: false, error: `Backend error: ${response.status}` };
				}

				const data = await response.json();
				return data;
			} catch (err) {
				console.error("[Auth] Network error calling backend:", err);
				return {
					success: false,
					error: `Network error: ${err instanceof Error ? err.message : 'Unknown error'}`
				};
			}
		},
		[],
	);

	const silentReauthenticate = useCallback(async (): Promise<string | null> => {
		try {
			const storedProvider = (localStorage.getItem(PROVIDER_STORAGE_KEY) as LoginProvider) || 'microsoft';
			console.log("[Auth] Attempting silent re-authentication...");
			const providerToken = await acquireProviderToken(storedProvider);
			const authResponse = await exchangeToken(providerToken, storedProvider);

			if (!authResponse.success || !authResponse.token || !authResponse.user) {
				console.error("[Auth] Silent re-auth exchange failed:", authResponse.error);
				return null;
			}

			localStorage.setItem(TOKEN_STORAGE_KEY, authResponse.token);
			setState({
				user: authResponse.user,
				token: authResponse.token,
				isLoading: false,
				isAuthenticated: true,
				error: null,
			});

			console.log("[Auth] Silent re-authentication successful");
			return authResponse.token;
		} catch (error) {
			console.error("[Auth] Silent re-authentication failed:", error);
			return null;
		}
	}, [acquireProviderToken, exchangeToken]);

	const authenticatedFetch = useCallback(async (url: string, init?: RequestInit): Promise<Response> => {
		const response = await apiFetch(url, init);

		if (response.status !== 401) {
			return response;
		}

		const newToken = await silentReauthenticate();
		if (!newToken) {
			return response;
		}

		const retryHeaders = new Headers(init?.headers);
		retryHeaders.set("Authorization", `Bearer ${newToken}`);
		return apiFetch(url, { ...init, headers: retryHeaders });
	}, [silentReauthenticate]);

	const login = useCallback(async (provider: LoginProvider) => {
		setState((prev) => ({ ...prev, error: null }));

		try {
			const providerToken = await acquireProviderToken(provider);
			// Spinner starts here — only during the backend exchange, not while popup is open
			setState((prev) => ({ ...prev, isLoading: true }));
			const authResponse = await exchangeToken(providerToken, provider);

			if (!authResponse.success || !authResponse.token || !authResponse.user) {
				throw new Error(authResponse.error || "Token exchange failed - no session received");
			}

			localStorage.setItem(TOKEN_STORAGE_KEY, authResponse.token);
			localStorage.setItem(PROVIDER_STORAGE_KEY, provider);

			Sentry.setUser({ id: authResponse.user.id });
			Sentry.metrics.count('user_logged_in', 1, { attributes: { provider } });
			setState({
				user: authResponse.user,
				token: authResponse.token,
				isLoading: false,
				isAuthenticated: true,
				error: null,
			});
		} catch (error) {
			if (isUserCancellation(error)) {
				setState((prev) => ({ ...prev, isLoading: false, error: null }));
				return;
			}
			const message = error instanceof Error ? error.message : "Login failed";
			console.error("[Auth] Login error:", error);
			Sentry.metrics.count('user_login_failed', 1, { attributes: { provider } });
			setState((prev) => ({ ...prev, isLoading: false, error: message }));
		}
	}, [acquireProviderToken, exchangeToken]);

	const refreshUser = useCallback(async () => {
		const token = getToken();
		if (!token) return;

		try {
			const response = await apiFetch(
				`${API_BASE_URL}/auth/me`,
				{ headers: { Authorization: `Bearer ${token}` } },
			);

			if (response.ok) {
				const data: AuthResponse = await response.json();
				if (data.success && data.user) {
					setState((prev) => ({ ...prev, user: data.user! }));
				}
			} else if (response.status === 401) {
				localStorage.removeItem(TOKEN_STORAGE_KEY);
				localStorage.removeItem(PROVIDER_STORAGE_KEY);
				queryClient.clear();
				setState({
					user: null,
					token: null,
					isLoading: false,
					isAuthenticated: false,
					error: null,
				});
			}
		} catch {
			// Silently fail - user state remains as-is
		}
	}, [getToken]);

	const logout = useCallback(async () => {
		const token = getToken();

		Sentry.metrics.count('user_logged_out', 1);
		localStorage.removeItem(TOKEN_STORAGE_KEY);
		localStorage.removeItem(PROVIDER_STORAGE_KEY);
		queryClient.clear();
		Sentry.setUser(null);

		try {
			if (token) {
				await apiFetch(`${API_BASE_URL}/auth/logout`, {
					method: "POST",
					headers: {
						Authorization: `Bearer ${token}`,
						"Content-Type": "application/json",
					},
				});
			}
		} catch {
			// Ignore logout errors
		}

		setState({
			user: null,
			token: null,
			isLoading: false,
			isAuthenticated: false,
			error: null,
		});
	}, [getToken]);

	useEffect(() => {
		const checkExistingSession = async () => {
			const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);

			if (!storedToken) {
				setState((prev) => ({ ...prev, isLoading: false }));
				return;
			}

			try {
				const response = await apiFetch(
					`${API_BASE_URL}/auth/me`,
					{ headers: { Authorization: `Bearer ${storedToken}` } },
				);

				if (response.ok) {
					const data: AuthResponse = await response.json();
					if (data.success && data.user) {
						Sentry.setUser({ id: data.user.id });
						Sentry.metrics.count('session_restored', 1);
						setState({
							user: data.user,
							token: storedToken,
							isLoading: false,
							isAuthenticated: true,
							error: null,
						});
						return;
					}
				}

				localStorage.removeItem(TOKEN_STORAGE_KEY);
				setState((prev) => ({ ...prev, isLoading: false }));
			} catch {
				// Network error - keep token but mark as not verified
				setState((prev) => ({ ...prev, isLoading: false }));
			}
		};

		checkExistingSession();
	}, []);

	const value: AuthContextValue = {
		...state,
		login,
		logout,
		getToken,
		refreshUser,
		authenticatedFetch,
	};

	return (
		<AuthContext.Provider value={value}>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuth(): AuthContextValue {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}
